import React from "react";
import PropTypes from "prop-types";
import AnimatedRadio from "./AnimatedRadio";
import { cn } from "../../utils/classNames";

const RadioGroup = React.forwardRef(
  ({ 
    className,
    children,
    name,
    value,
    onChange,
    disabled = false,
    size = "md",
    direction = "horizontal", // "horizontal" or "vertical"
    ...props 
  }, ref) => {
    
    const directionClasses = {
      horizontal: "flex flex-wrap gap-4",
      vertical: "flex flex-col gap-3"
    };

    return (
      <div 
        className={cn(directionClasses[direction], className)}
        ref={ref}
        role="radiogroup"
        {...props}
      >
        {React.Children.map(children, (child, index) => {
          if (React.isValidElement(child) && child.type === RadioGroup.Option) {
            return React.cloneElement(child, {
              name,
              checked: child.props.value === value,
              onChange: (e) => onChange?.(e.target.value),
              disabled: disabled || child.props.disabled,
              size,
              key: child.props.value || index
            });
          }
          return child;
        })}
      </div>
    );
  }
);

// RadioGroup.Option component
const RadioOption = React.forwardRef(
  ({ 
    className,
    children,
    value,
    disabled = false,
    id,
    ...props
  }, ref) => {
    const optionId = id || `radio-${value}`;
    
    return (
      <div className={cn("flex items-center space-x-3", className)}>
        <AnimatedRadio
          id={optionId}
          value={value}
          disabled={disabled}
          ref={ref}
          {...props}
        />
        {children && (
          <label
            htmlFor={optionId}
            className={cn(
              "text-body-small font-semibold leading-none select-none",
              disabled ? "cursor-not-allowed opacity-70" : "cursor-pointer hover:text-primary"
            )}
          >
            {children}
          </label>
        )}
      </div>
    );
  }
);

RadioGroup.displayName = "RadioGroup";
RadioOption.displayName = "RadioOption";

// Attach Option as a static property
RadioGroup.Option = RadioOption;

RadioGroup.propTypes = {
  className: PropTypes.string,
  children: PropTypes.node,
  name: PropTypes.string.isRequired,
  value: PropTypes.string,
  onChange: PropTypes.func,
  disabled: PropTypes.bool,
  size: PropTypes.oneOf(["sm", "md", "lg"]),
  direction: PropTypes.oneOf(["horizontal", "vertical"]),
};

RadioOption.propTypes = {
  className: PropTypes.string,
  children: PropTypes.node,
  value: PropTypes.string.isRequired,
  disabled: PropTypes.bool,
  id: PropTypes.string,
};

export default RadioGroup;
